<template>
  <div class="editor-section">
    <!-- 區塊版型選擇（優先於替代文字與底圖） -->
    <div class="form-group">
      <label class="form-label">區塊分切版型</label>
      <div class="layout-current-row">
        <div class="layout-current-meta">
          <div
            v-if="selectedLayout && LAYOUTS[selectedLayout]"
            class="layout-current-thumb-wrap"
            :title="LAYOUTS[selectedLayout].label"
          >
            <div
              class="layout-current-thumb"
              :style="layoutSummaryThumbBoxStyle"
            >
              <div
                v-for="(a, i) in LAYOUTS[selectedLayout].areas"
                :key="i"
                class="layout-current-thumb-cell"
                :style="{
                  left: (a.x * 100) + '%',
                  top: (a.y * 100) + '%',
                  width: (a.w * 100) + '%',
                  height: (a.h * 100) + '%',
                  background: BADGE[i],
                }"
              />
            </div>
          </div>
          <div v-else class="layout-current-thumb-wrap layout-current-thumb-placeholder" aria-hidden="true">
            <i class="ri-layout-grid-line"></i>
          </div>
          <div class="layout-current-info">
            <template v-if="selectedLayout && LAYOUTS[selectedLayout]">
              <span class="layout-current-name">{{ LAYOUTS[selectedLayout].label }}</span>
              <span class="layout-current-size">寬 1040px × 高 {{ effectiveBaseHeight }}px</span>
            </template>
            <span v-else class="layout-current-placeholder">尚未選擇版型</span>
          </div>
        </div>
        <button type="button" class="btn btn-primary btn-open-picker" @click="openLayoutPicker">
          <i class="ri-layout-grid-line"></i> 選擇版型
        </button>
      </div>
    </div>

    <!-- 選擇版型 Modal -->
    <div v-if="layoutPickerOpen" class="layout-picker-backdrop" @mousedown.self="closeLayoutPicker">
      <div class="layout-picker-modal" role="dialog" aria-labelledby="layout-picker-title">
        <div class="layout-picker-header">
          <h3 id="layout-picker-title" class="layout-picker-title">選擇版型</h3>
          <button type="button" class="layout-picker-close" aria-label="關閉" @click="closeLayoutPicker">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="layout-picker-body">
          <nav class="layout-picker-sidebar">
            <button
              v-for="c in pickerNavItems"
              :key="c.id"
              type="button"
              class="layout-picker-nav-item"
              :class="{ active: pickerCategoryId === c.id }"
              @click="pickerCategoryId = c.id"
            >
              {{ c.label }}
            </button>
          </nav>
          <div class="layout-picker-main">
            <div v-for="(grp, gi) in pickerCategoryGroups" :key="gi" class="layout-picker-section">
              <h4 class="layout-picker-section-title">{{ grp.label }}</h4>
              <div class="layout-picker-grid">
                <button
                  v-for="key in grp.keys"
                  :key="key"
                  type="button"
                  class="layout-picker-option"
                  :class="{
                    selected: pendingLayoutKey === key && pendingGroupHeight === grp.height
                  }"
                  :title="LAYOUTS[key].label"
                  @click="pickPending(key, grp.height)"
                >
                  <div
                    class="layout-picker-thumb"
                    :style="{ aspectRatio: `1040 / ${grp.height}` }"
                  >
                    <div
                      v-for="(a, i) in LAYOUTS[key].areas"
                      :key="i"
                      class="layout-picker-cell"
                      :style="{
                        left: (a.x * 100) + '%',
                        top: (a.y * 100) + '%',
                        width: (a.w * 100) + '%',
                        height: (a.h * 100) + '%',
                        background: BADGE[i],
                      }"
                    />
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="layout-picker-footer">
          <button type="button" class="btn btn-cancel-outline" @click="closeLayoutPicker">取消</button>
          <button
            type="button"
            class="btn btn-line-primary"
            :disabled="!pendingLayoutKey"
            @click="confirmLayoutPicker"
          >
            選擇
          </button>
        </div>
      </div>
    </div>

    <!-- 替代文字（於 Editor 中僅圖文類型由此元件編輯） -->
    <div class="form-group">
      <label class="form-label required-label">替代文字（AltText）</label>
      <input
        :value="altText"
        type="text"
        class="form-control"
        placeholder="無法顯示圖文時的替代說明"
        maxlength="400"
        @input="onAltTextInput"
      />
    </div>

    <!-- 選定版型後：底圖與高度（含尺寸提示） -->
    <template v-if="selectedLayout">
      <p class="imagemap-size-hint">
        請製作底圖為 <strong>寬 1040px × 高 {{ effectiveBaseHeight }}px</strong>，與所選「{{ LAYOUTS[selectedLayout].label }}」版型一致；上傳後區塊熱點會依此尺寸換算。
      </p>
      <div class="form-group">
        <label class="form-label required-label">圖文底圖</label>
        <ImageUploader
          :modelValue="modelValue.baseUrl"
          @update:modelValue="val => emitUpdate({ baseUrl: val })"
          :upload-fn="uploadFn"
          shape="card"
          :aspect-ratio="imagemapPreviewAspect"
          accept="image/jpeg,image/png"
          placeholder="上傳圖文底圖"
          :hint="imagemapUploaderHint"
          size-hint="最大 10MB。比例正確則直接上傳；不符時會開啟裁切，輸出 1040×高 像素"
          :crop-width="1040"
          :crop-height="effectiveBaseHeight"
          :crop-only-if-aspect-mismatch="true"
          :max-size-mb="10"
        />
      </div>
      <div class="form-group">
        <label class="form-label required-label">圖片高度（px，寬度固定 1040）</label>
        <input
          type="number"
          class="form-control"
          :value="modelValue.baseHeight"
          @input="updateHeight(Number($event.target.value))"
          placeholder="1040"
          min="1"
          style="max-width: 200px;"
        />
        <p class="form-hint-sub">請與實際底圖高度一致。若修改此數值，各區塊的像素座標會依比例重算。</p>
      </div>
    </template>

    <!-- 視覺化區塊預覽 -->
    <div v-if="selectedLayout && modelValue.baseUrl" class="form-group">
      <label class="form-label">區塊預覽（點擊區塊可定位到連結設定）</label>
      <div class="imagemap-canvas">
        <img :src="modelValue.baseUrl" alt="" />
        <div
          v-for="(a, i) in currentAreas"
          :key="i"
          class="area-overlay"
          :class="{ active: activeIdx === i }"
          :style="{
            left: (a.x * 100) + '%',
            top: (a.y * 100) + '%',
            width: (a.w * 100) + '%',
            height: (a.h * 100) + '%',
          }"
          @click="focusArea(i)"
        >
          <span class="area-num" :style="{ background: BADGE[i] }">{{ i + 1 }}</span>
        </div>
      </div>
    </div>

    <!-- 各區塊連結設定 -->
    <div v-if="selectedLayout" class="form-group">
      <label class="form-label required-label">各區塊連結設定</label>
      <div class="area-list">
        <div
          v-for="(lk, i) in links"
          :key="i"
          class="area-row"
          :class="{ active: activeIdx === i }"
        >
          <span class="area-badge" :style="{ background: BADGE[i] }">{{ i + 1 }}</span>
          <div class="row-fields">
            <input
              :ref="el => linkRefs[i] = el"
              v-model="links[i].linkUri"
              type="url"
              class="form-control"
              placeholder="https://example.com"
              @focus="activeIdx = i"
              @input="syncJson"
            />
            <input
              v-model="links[i].label"
              type="text"
              class="form-control lbl"
              placeholder="標籤（選填）"
              @focus="activeIdx = i"
              @input="syncJson"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted, watch } from 'vue';
import ImageUploader from '@/components/ImageUploader.vue';
import { lineMsgTemplateService } from '@/api/modules/lineMsgTemplates.js';

const props = defineProps({
  modelValue: { type: Object, required: true },
  altText: { type: String, default: '' }
});
const emit = defineEmits(['update:modelValue', 'update:altText']);

function onAltTextInput(e) {
  emit('update:altText', e.target.value);
}

const uploadFn = (file) => lineMsgTemplateService.uploadImage(file);

const ALL_LAYOUT_KEYS = ['full', 'h2', 'v2', 'h3', 'quad', 't1b2', 't2b1', 'six'];

/** LINE 圖文常見寬高（寬固定 1040）與建議版型分組 */
const LAYOUT_CATEGORIES = [
  {
    id: 'square',
    label: '正方形',
    groups: [
      {
        label: '寬1040px × 高1040px',
        height: 1040,
        keys: ALL_LAYOUT_KEYS
      }
    ]
  },
  {
    id: 'landscape',
    label: '橫長',
    groups: [
      { label: '寬1040px × 高350px', height: 350, keys: ['full', 'h2', 'v2'] },
      { label: '寬1040px × 高700px', height: 700, keys: ['full', 'h2', 'v2', 'h3', 't1b2', 't2b1'] },
      { label: '寬1040px × 高585px', height: 585, keys: ['full', 'h2', 'v2', 'h3', 'quad', 't1b2', 't2b1'] }
    ]
  },
  {
    id: 'portrait',
    label: '縱長',
    groups: [
      { label: '寬1040px × 高1300px', height: 1300, keys: ['full', 'h2', 'v2', 'h3', 'quad', 't1b2', 't2b1'] },
      { label: '寬1040px × 高1850px', height: 1850, keys: ALL_LAYOUT_KEYS }
    ]
  }
];

const LAYOUTS = {
  full: { label: '滿版', areas: [{ x: 0, y: 0, w: 1, h: 1 }] },
  h2:   { label: '上下二分', areas: [{ x: 0, y: 0, w: 1, h: .5 }, { x: 0, y: .5, w: 1, h: .5 }] },
  v2:   { label: '左右二分', areas: [{ x: 0, y: 0, w: .5, h: 1 }, { x: .5, y: 0, w: .5, h: 1 }] },
  h3:   { label: '三等分', areas: [{ x: 0, y: 0, w: 1, h: 1/3 }, { x: 0, y: 1/3, w: 1, h: 1/3 }, { x: 0, y: 2/3, w: 1, h: 1/3 }] },
  quad: { label: '四宮格', areas: [{ x: 0, y: 0, w: .5, h: .5 }, { x: .5, y: 0, w: .5, h: .5 }, { x: 0, y: .5, w: .5, h: .5 }, { x: .5, y: .5, w: .5, h: .5 }] },
  t1b2: { label: '上1下2', areas: [{ x: 0, y: 0, w: 1, h: .5 }, { x: 0, y: .5, w: .5, h: .5 }, { x: .5, y: .5, w: .5, h: .5 }] },
  t2b1: { label: '上2下1', areas: [{ x: 0, y: 0, w: .5, h: .5 }, { x: .5, y: 0, w: .5, h: .5 }, { x: 0, y: .5, w: 1, h: .5 }] },
  six:  { label: '六宮格', areas: [
    { x: 0, y: 0, w: 1/3, h: .5 }, { x: 1/3, y: 0, w: 1/3, h: .5 }, { x: 2/3, y: 0, w: 1/3, h: .5 },
    { x: 0, y: .5, w: 1/3, h: .5 }, { x: 1/3, y: .5, w: 1/3, h: .5 }, { x: 2/3, y: .5, w: 1/3, h: .5 }
  ]}
};

const BADGE = ['#3B82F6', '#EF4444', '#22C55E', '#F59E0B', '#A855F7', '#EC4899', '#14B8A6', '#F97316', '#6366F1'];

const pickerNavItems = [
  ...LAYOUT_CATEGORIES.map(c => ({ id: c.id, label: c.label })),
  { id: 'custom', label: '自訂' }
];

const selectedLayout = ref(null);
const links = ref([]);
const activeIdx = ref(-1);
const linkRefs = ref([]);

const layoutPickerOpen = ref(false);
const pickerCategoryId = ref('square');
const pendingLayoutKey = ref(null);
const pendingGroupHeight = ref(1040);

const currentAreas = computed(() =>
  selectedLayout.value && LAYOUTS[selectedLayout.value] ? LAYOUTS[selectedLayout.value].areas : []
);

/** 主畫面版型縮略圖外框（限制最大尺寸，縱長／橫長比例仍正確） */
const effectiveBaseHeight = computed(() => Math.max(1, Number(props.modelValue.baseHeight) || 1040));

/** 底圖預覽外框與下方「區塊預覽」同為 1040×baseHeight 比例（寬度仍為 card 320px） */
const imagemapPreviewAspect = computed(() => `1040 / ${effectiveBaseHeight.value}`);

const imagemapUploaderHint = computed(() =>
  `僅支援 JPEG / PNG。建議 寬 1040px × 高 ${effectiveBaseHeight.value}px；寬高比不符時可拖曳裁切後再上傳`
);

const layoutSummaryThumbBoxStyle = computed(() => {
  const imgW = 1040;
  const imgH = effectiveBaseHeight.value;
  const maxW = 72;
  const maxH = 88;
  let tw = maxW;
  let th = (tw * imgH) / imgW;
  if (th > maxH) {
    th = maxH;
    tw = (th * imgW) / imgH;
  }
  return {
    width: `${Math.round(tw)}px`,
    height: `${Math.round(th)}px`
  };
});

const pickerCategoryGroups = computed(() => {
  if (pickerCategoryId.value === 'custom') {
    const h = Number(props.modelValue.baseHeight) || 1040;
    return [{
      label: `寬1040px × 高${h}px（依「圖片高度」欄位）`,
      height: h,
      keys: ALL_LAYOUT_KEYS
    }];
  }
  const cat = LAYOUT_CATEGORIES.find(c => c.id === pickerCategoryId.value);
  return cat ? cat.groups : [];
});

function emitUpdate(changes) {
  emit('update:modelValue', { ...props.modelValue, ...changes });
}

function inferCategoryId(height) {
  const h = Number(height) || 1040;
  if (h === 1040) return 'square';
  if (h < 1040) return 'landscape';
  return 'portrait';
}

function openLayoutPicker() {
  layoutPickerOpen.value = true;
  pickerCategoryId.value = inferCategoryId(props.modelValue.baseHeight);
  pendingLayoutKey.value = selectedLayout.value;
  pendingGroupHeight.value = Number(props.modelValue.baseHeight) || 1040;
  document.body.style.overflow = 'hidden';
}

function closeLayoutPicker() {
  layoutPickerOpen.value = false;
  document.body.style.overflow = '';
}

function pickPending(key, height) {
  pendingLayoutKey.value = key;
  pendingGroupHeight.value = height;
}

function confirmLayoutPicker() {
  if (!pendingLayoutKey.value) return;
  applyLayout(pendingLayoutKey.value, pendingGroupHeight.value);
  closeLayoutPicker();
}

function buildActionsJson(layoutKey, height) {
  const layout = LAYOUTS[layoutKey];
  if (!layout) return '[]';
  const W = 1040;
  const H = height || 1040;
  return JSON.stringify(layout.areas.map((a, i) => ({
    type: 'uri',
    label: links.value[i]?.label || `區域 ${i + 1}`,
    linkUri: links.value[i]?.linkUri || 'https://example.com',
    area: {
      x: Math.round(a.x * W),
      y: Math.round(a.y * H),
      width: Math.round(a.w * W),
      height: Math.round(a.h * H)
    }
  })), null, 2);
}

function applyLayout(layoutKey, height) {
  const H = (height ?? Number(props.modelValue.baseHeight)) || 1040;
  selectedLayout.value = layoutKey;
  const count = LAYOUTS[layoutKey].areas.length;
  const old = [...links.value];
  links.value = Array.from({ length: count }, (_, i) => ({
    linkUri: old[i]?.linkUri || '',
    label: old[i]?.label || ''
  }));
  activeIdx.value = 0;
  emitUpdate({
    layout: layoutKey,
    baseHeight: H,
    areaLinks: [...links.value],
    actionsJson: buildActionsJson(layoutKey, H)
  });
}

function focusArea(i) {
  activeIdx.value = i;
  nextTick(() => { linkRefs.value[i]?.focus(); });
}

function syncJson() {
  if (!selectedLayout.value) return;
  emitUpdate({
    actionsJson: buildActionsJson(selectedLayout.value, props.modelValue.baseHeight),
    areaLinks: [...links.value]
  });
}

function updateHeight(val) {
  const changes = { baseHeight: val };
  if (selectedLayout.value) {
    changes.actionsJson = buildActionsJson(selectedLayout.value, val);
  }
  emitUpdate(changes);
}

function onPickerKeydown(e) {
  if (e.key === 'Escape' && layoutPickerOpen.value) {
    closeLayoutPicker();
  }
}

onMounted(() => {
  if (props.modelValue.layout && LAYOUTS[props.modelValue.layout]) {
    selectedLayout.value = props.modelValue.layout;
    const count = LAYOUTS[props.modelValue.layout].areas.length;
    const saved = props.modelValue.areaLinks || [];
    links.value = Array.from({ length: count }, (_, i) => ({
      linkUri: saved[i]?.linkUri || '',
      label: saved[i]?.label || ''
    }));
  }
  window.addEventListener('keydown', onPickerKeydown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', onPickerKeydown);
  document.body.style.overflow = '';
});

watch(
  () => props.modelValue.layout,
  (key) => {
    if (key && LAYOUTS[key] && key !== selectedLayout.value) {
      selectedLayout.value = key;
      const count = LAYOUTS[key].areas.length;
      const saved = props.modelValue.areaLinks || [];
      links.value = Array.from({ length: count }, (_, i) => ({
        linkUri: saved[i]?.linkUri || '',
        label: saved[i]?.label || ''
      }));
    }
  }
);
</script>

<style scoped>
.layout-current-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f9fafb;
}
.layout-current-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
}
.layout-current-thumb-wrap {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.layout-current-thumb {
  position: relative;
  flex-shrink: 0;
  background: #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #d1d5db;
}
.layout-current-thumb-cell {
  position: absolute;
  box-sizing: border-box;
  border: 1px solid rgba(255, 255, 255, 0.7);
  opacity: 0.92;
}
.layout-current-thumb-placeholder {
  width: 72px;
  height: 56px;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  background: #fff;
  color: #c4c4c4;
  font-size: 28px;
  align-items: center;
  justify-content: center;
}
.layout-current-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 0 1 auto;
}
.layout-current-name {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}
.layout-current-size {
  font-size: 12px;
  color: #6b7280;
}
.layout-current-placeholder {
  font-size: 13px;
  color: #9ca3af;
}
.btn-open-picker {
  flex-shrink: 0;
  margin-left: auto;
}

/* Modal */
.layout-picker-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10050;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.layout-picker-modal {
  width: min(920px, 100%);
  max-height: min(88vh, 720px);
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.layout-picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
}
.layout-picker-title {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  color: #111827;
}
.layout-picker-close {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #6b7280;
  font-size: 22px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;
}
.layout-picker-close:hover {
  background: #f3f4f6;
  color: #374151;
}
.layout-picker-body {
  display: flex;
  min-height: 0;
  flex: 1;
}
.layout-picker-sidebar {
  width: 132px;
  flex-shrink: 0;
  border-right: 1px solid #e5e7eb;
  padding: 8px 0;
  background: #fafafa;
}
.layout-picker-nav-item {
  display: block;
  width: 100%;
  padding: 12px 16px;
  border: none;
  border-bottom: 1px solid #eee;
  background: transparent;
  text-align: left;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: background 0.12s;
}
.layout-picker-nav-item:hover {
  background: #f0f0f0;
}
.layout-picker-nav-item.active {
  background: #e8e8e8;
  color: #111;
}
.layout-picker-main {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px 20px;
  min-width: 0;
}
.layout-picker-section {
  margin-bottom: 22px;
}
.layout-picker-section:last-child {
  margin-bottom: 0;
}
.layout-picker-section-title {
  margin: 0 0 12px;
  font-size: 13px;
  font-weight: 600;
  color: #4b5563;
}
.layout-picker-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.layout-picker-option {
  padding: 8px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.layout-picker-option:hover {
  border-color: #86efac;
}
.layout-picker-option.selected {
  border-color: #06c755;
  background: #f0fff4;
  box-shadow: 0 0 0 1px rgba(6, 199, 85, 0.25);
}
.layout-picker-thumb {
  position: relative;
  width: 72px;
  max-height: 120px;
  margin: 0 auto;
  background: #f3f4f6;
  border-radius: 4px;
  overflow: hidden;
}
.layout-picker-cell {
  position: absolute;
  box-sizing: border-box;
  border: 1px solid rgba(255, 255, 255, 0.65);
  opacity: 0.88;
}
.layout-picker-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid #e5e7eb;
  background: #fafafa;
}
.btn-cancel-outline {
  padding: 8px 20px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.btn-cancel-outline:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}
.btn-line-primary {
  padding: 8px 22px;
  border-radius: 8px;
  border: none;
  background: #06c755;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s, opacity 0.15s;
}
.btn-line-primary:hover:not(:disabled) {
  filter: brightness(0.95);
}
.btn-line-primary:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* 圖片區塊預覽（與上方 ImageUploader shape=card 同寬 320px） */
.imagemap-canvas {
  position: relative;
  max-width: min(320px, 100%);
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
}
.imagemap-canvas img {
  width: 100%;
  display: block;
}
.area-overlay {
  position: absolute;
  background: rgba(59, 130, 246, .18);
  border: 2px dashed rgba(59, 130, 246, .45);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background .15s, border-color .15s;
  box-sizing: border-box;
}
.area-overlay:hover,
.area-overlay.active {
  background: rgba(59, 130, 246, .32);
  border-color: rgba(59, 130, 246, .8);
  border-style: solid;
}
.area-num {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(0, 0, 0, .25);
}

/* 區塊連結清單 */
.area-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.area-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  transition: border-color .15s, background .15s;
}
.area-row.active {
  border-color: #93c5fd;
  background: #f0f7ff;
}
.area-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  margin-top: 5px;
}
.row-fields {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.row-fields .lbl { max-width: 220px; }

.imagemap-size-hint {
  margin: 0 0 14px;
  padding: 12px 14px;
  font-size: 13px;
  line-height: 1.5;
  color: #4b5563;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
}
.imagemap-size-hint strong { color: #0369a1; }
.form-hint-sub {
  margin: 6px 0 0;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.45;
}
</style>
